'use client';

import {
  Badge,
  Button,
  Card,
  FilterBar,
  type FilterConfig,
  type FilterValue,
  Scroll,
  Spinner,
  Text,
} from '@terra/ui-design-system';
import { Box, Grid, GridItem, HStack, VStack } from '@terra/ui-styled-system';
import { SparklesIcon } from '@terra/ui-icons';
import { useMemo, useState } from 'react';
import type { SkillSummaryDto } from '@terra/shared-contracts-ai-skills';
import {
  COPILOT_SKILL_AGENT_IDS,
  SkillStatusSchema,
} from '@terra/shared-contracts-ai-skills';
import { useSkills } from '../hooks/use-skills';
import type {
  SkillsAuditLabels,
  SkillsDetailLabels,
  SkillsEditorLabels,
  SkillsExplainerLabels,
  SkillsListLabels,
  SkillsVersionLabels,
} from '../types';
import { SkillEditorDrawer } from './skill-editor-drawer';
import { SkillDetailDrawer } from './skill-detail-drawer';
import { SkillsIntroBlock } from './skills-explainer';

type StatusFilter = 'all' | 'PUBLISHED' | 'DRAFT';
const STATUS_OPTIONS = SkillStatusSchema.options;
const STATUS_FILTER_ID = 'status';

export function SkillsManagerPanel({
  listLabels,
  editorLabels,
  versionLabels,
  auditLabels,
  explainerLabels,
  detailLabels,
  formatDate,
  applicationId,
  onOpenSkillEditor,
  onOpenSkillDetail,
  showExplainer = true,
  showHeader = false,
  canCreate = true,
  canEdit = true,
  scrollContent,
}: {
  listLabels: SkillsListLabels;
  editorLabels: SkillsEditorLabels;
  versionLabels: SkillsVersionLabels;
  auditLabels: SkillsAuditLabels;
  explainerLabels: SkillsExplainerLabels;
  detailLabels: SkillsDetailLabels;
  formatDate: (date: Date) => string;
  applicationId: string;
  onOpenSkillEditor?: (request: {
    mode: 'create' | 'edit';
    skillId?: string;
    initialDraft?: {
      slug: string;
      name: string;
      description: string;
      body: string;
    };
  }) => void;
  onOpenSkillDetail?: (skill: SkillSummaryDto) => void;
  showExplainer?: boolean;
  showHeader?: boolean;
  canCreate?: boolean;
  canEdit?: boolean;
  scrollContent?: boolean;
}) {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [nameSearch, setNameSearch] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [selectedSkillId, setSelectedSkillId] = useState<string | null>(null);
  const { skills, isLoading } = useSkills({
    applicationId,
    source: 'CUSTOMER',
    status: statusFilter === 'all' ? undefined : statusFilter,
  });
  const hasActiveFilters =
    statusFilter !== 'all' || nameSearch.trim().length > 0;
  const copilotAgentIds = useMemo(
    () => new Set<string>(COPILOT_SKILL_AGENT_IDS),
    [],
  );
  const visibleSkills = useMemo(
    () => skills.filter((skill) => copilotAgentIds.has(skill.agentId)),
    [copilotAgentIds, skills],
  );
  const filteredSkills = useMemo(() => {
    const query = nameSearch.trim().toLowerCase();

    if (!query) {
      return visibleSkills;
    }

    return visibleSkills.filter((skill) =>
      [skill.name, skill.slug, skill.description]
        .join('\n')
        .toLowerCase()
        .includes(query),
    );
  }, [nameSearch, visibleSkills]);
  const filterConfigs: FilterConfig[] = useMemo(
    () => [
      {
        id: STATUS_FILTER_ID,
        items: STATUS_OPTIONS.map((status) => ({
          value: status,
          label: status === 'DRAFT' ? listLabels.draft : listLabels.published,
        })),
        placeholder: listLabels.allStatuses,
      },
    ],
    [listLabels],
  );
  const filterValue: FilterValue = useMemo(() => {
    const value: FilterValue = [];

    if (statusFilter !== 'all') {
      value.push({ id: STATUS_FILTER_ID, value: [statusFilter] });
    }

    return value;
  }, [statusFilter]);
  const handleFilterChange = (value: FilterValue) => {
    const nextStatus = value.find((item) => item.id === STATUS_FILTER_ID)
      ?.value[0];

    setStatusFilter((nextStatus ?? 'all') as StatusFilter);
  };

  const content = isLoading ? (
    <HStack justifyContent="center" py={8}>
      <Spinner />
      <Text fontSize="xs">{listLabels.loading}</Text>
    </HStack>
  ) : filteredSkills.length === 0 ? (
    <Card.Root
      w="full"
      boxShadow="none"
      borderWidth="1px"
      borderStyle="solid"
      borderColor="border.neutral.muted"
    >
      <Card.Body py={8}>
        {hasActiveFilters ? (
          <Text color="fg.muted" textAlign="center" fontSize="xs">
            {listLabels.noResults}
          </Text>
        ) : (
          <VStack gap={2} alignItems="center" textAlign="center">
            <Box
              p={2}
              borderRadius="full"
              bg="bg.neutral.subtleSoft"
              color="fg.muted"
            >
              <SparklesIcon size={16} />
            </Box>
            <VStack gap={0.5}>
              <Text fontSize="sm" fontWeight="semibold" color="fg.default">
                {listLabels.emptyTitle}
              </Text>
              <Text fontSize="xs" color="fg.muted" maxW="420px">
                {listLabels.emptyBody}
              </Text>
            </VStack>
            {canCreate ? (
              <Button
                size="xs"
                h="7"
                onClick={() =>
                  onOpenSkillEditor
                    ? onOpenSkillEditor({ mode: 'create' })
                    : setEditorOpen(true)
                }
              >
                {listLabels.emptyCta}
              </Button>
            ) : null}
            <Text fontSize="2xs" color="fg.muted" mt={1}>
              {listLabels.tenantScopeNote}
            </Text>
          </VStack>
        )}
      </Card.Body>
    </Card.Root>
  ) : (
    <Grid w="full" columns={{ base: 1, md: 2, xl: 3 }} gap={2.5}>
      {filteredSkills.map((skill) => (
        <GridItem key={skill.skillId}>
          <SkillCatalogCard
            skill={skill}
            listLabels={listLabels}
            onClick={() =>
              onOpenSkillDetail
                ? onOpenSkillDetail(skill)
                : setSelectedSkillId(skill.skillId)
            }
          />
        </GridItem>
      ))}
    </Grid>
  );

  return (
    <VStack
      gap={3}
      alignItems="stretch"
      w="full"
      h={scrollContent ? 'full' : undefined}
    >
      {showHeader ? (
        <VStack alignItems="flex-start" gap={0.5} w="full">
          <Text fontSize="sm" fontWeight="semibold" color="fg.default">
            {listLabels.title}
          </Text>
          <Text fontSize="xs" color="fg.muted">
            {listLabels.description}
          </Text>
        </VStack>
      ) : null}
      {showExplainer ? <SkillsIntroBlock labels={explainerLabels} /> : null}

      <FilterBar
        filters={filterConfigs}
        value={filterValue}
        onFilterChange={handleFilterChange}
        onSearch={setNameSearch}
        searchValue={nameSearch}
        searchPlaceholder={listLabels.searchPlaceholder}
        px={0}
        pb={0}
        showBottomBorder={false}
      />

      {scrollContent ? (
        <Scroll w="full" h="full">
          <Box pt={0}>{content}</Box>
        </Scroll>
      ) : (
        content
      )}

      {editorOpen && canCreate && !onOpenSkillEditor ? (
        <SkillEditorDrawer
          mode="create"
          labels={editorLabels}
          applicationId={applicationId}
          onClose={() => setEditorOpen(false)}
        />
      ) : null}
      {selectedSkillId ? (
        <SkillDetailDrawer
          skillId={selectedSkillId}
          listLabels={listLabels}
          editorLabels={editorLabels}
          versionLabels={versionLabels}
          auditLabels={auditLabels}
          detailLabels={detailLabels}
          formatDate={formatDate}
          applicationId={applicationId}
          onOpenSkillEditor={onOpenSkillEditor}
          canEdit={canEdit}
          onClose={() => setSelectedSkillId(null)}
        />
      ) : null}
    </VStack>
  );
}

function SkillCatalogCard({
  skill,
  listLabels,
  onClick,
}: {
  skill: SkillSummaryDto;
  listLabels: SkillsListLabels;
  onClick: () => void;
}) {
  return (
    <Card.Root
      h="full"
      cursor="pointer"
      role="group"
      boxShadow="none"
      borderWidth="1px"
      borderStyle="solid"
      borderColor="border.neutral.muted"
      borderRadius="sm"
      transition="background-color 0.18s ease-out, border-color 0.18s ease-out"
      _hover={{
        bg: 'bg.neutral.subtleSoft',
        borderColor: 'border.neutral.default',
      }}
      onClick={onClick}
    >
      <Card.Header px={3} py={2.5}>
        <HStack justifyContent="space-between" alignItems="start" gap={2}>
          <VStack alignItems="start" gap={1} minW="0" flex="1">
            <Card.Title fontSize="sm" color="fg.default" lineClamp={1}>
              {skill.name}
            </Card.Title>
            <Text
              fontSize="xs"
              color="fg.muted"
              lineClamp={2}
              lineHeight="1.45"
            >
              {skill.description || skill.slug}
            </Text>
          </VStack>
          {skill.deletedAt ? (
            <Badge variant="emphasis" colorScheme="red.9" size="sm">
              {listLabels.tombstoned}
            </Badge>
          ) : skill.publishedVersionId ? (
            <Badge variant="outline" colorScheme="green.9" size="sm">
              {listLabels.published}
            </Badge>
          ) : (
            <Badge variant="outline" size="sm">
              {listLabels.draft}
            </Badge>
          )}
        </HStack>
      </Card.Header>
      <Card.Body px={3} pt={0} pb={2.5}>
        <VStack alignItems="stretch" gap={1.5}>
          <HStack gap={1.5} flexWrap="wrap">
            <Badge
              variant={skill.source === 'PLATFORM' ? 'fill' : 'outline'}
              size="sm"
            >
              {skill.source === 'PLATFORM'
                ? listLabels.platform
                : listLabels.customer}
            </Badge>
            {skill.hasDraft ? (
              <Badge variant="outline" size="sm">
                {listLabels.draftBadgeLabel}
              </Badge>
            ) : null}
          </HStack>
        </VStack>
      </Card.Body>
    </Card.Root>
  );
}
